import { useEffect, useState } from 'react';
import { useStores } from 'hooks/useStores';
import { KPI, Template } from 'types/kpi';
import {
  useKpiTemplateListLazyQuery,
  useKpiTemplateAddMutation,
  useKpiTemplateDeleteMutation,
  useKpiTemplateUpdateMutation,
} from 'generated/graphql';
import { IBaseResponse } from 'types/common/baseResponse';

const ACCOUNT_ID = 'account id';

const NEW_TEMPLATE = {
  name: '',
  description: '',
  indicators: [],
};

const getNewTemplate = () => ({
  ...NEW_TEMPLATE,
  id: 'UUID' + new Date().getTime(),
});

const isTemplateIncluded = (
  template: any,
  { searchValue, filterId, kpisByIds }: any,
) => {
  const templateNameLowered = template.name.toLowerCase();
  const templateDescriptionLowered = template.description.toLowerCase();
  const searchValueLowered = searchValue.toLowerCase();
  const matchesTemplateFilter =
    filterId === 'All Templates' || filterId === template.name;
  const matchesTemplateName =
    filterId === 'All Templates' &&
    templateNameLowered.includes(searchValueLowered);
  const matchesTemplateDescription =
    filterId === 'All Templates' &&
    templateDescriptionLowered.includes(searchValueLowered);
  const hasNestedKPIMatch = template.indicators?.some(({ id: kpiId }: any) => {
    const kpi = kpisByIds[kpiId];
    if (!kpi) return false;
    const kpiNameLowered = kpi.name.toLowerCase();
    const kpiGroupLowered = kpi.group.toLowerCase();
    const kpiTypeLowered = kpi.type.toLowerCase();
    const kpiDescriptionLowered = kpi.description.toLowerCase();
    return (
      kpiNameLowered.includes(searchValueLowered) ||
      kpiGroupLowered.includes(searchValueLowered) ||
      kpiTypeLowered.includes(searchValueLowered) ||
      kpiDescriptionLowered.includes(searchValueLowered)
    );
  });
  return (
    matchesTemplateFilter &&
    (hasNestedKPIMatch || matchesTemplateName || matchesTemplateDescription)
  );
};

export function useTemplates() {
  const [getTemplates, { data }] = useKpiTemplateListLazyQuery();
  const [addTemplate] = useKpiTemplateAddMutation();
  const [deleteTemplate] = useKpiTemplateDeleteMutation();
  const [updateTemplate] = useKpiTemplateUpdateMutation();
  const { KPIStore } = useStores();
  const [searchValue, setSearchValue] = useState('');
  const [editorSearch, setEditorSearch] = useState('');
  const [filterId, setFilterId] = useState('All Templates');
  const [openKPIDrawer, setOpenKPIDrawer] = useState(false);

  useEffect(() => {
    if (data && data.kpiTemplateList) {
      // @ts-ignore
      KPIStore.templates = data.kpiTemplateList;
    }
  }, [data?.kpiTemplateList]);

  return {
    operations: {
      handleSearch: (e: any) => {
        setSearchValue(e.target.value);
      },
      handleGroupsChange: (e: any) => {
        KPIStore.templates = KPIStore.templates.map((t) => ({
          ...t,
          expanded: e.target.value !== 'All Templates',
        }));
        setFilterId(e.target.value);
      },
      openEditKPIDrawer: ({ row }: any) => {
        KPIStore.editorTemplate = {
          ...row.original,
          indicators: row.original.indicators.map(({ id }: any) => id),
        };
        setOpenKPIDrawer(true);
      },
      openNewKPIDrawer: () => {
        KPIStore.editorTemplate = getNewTemplate();
        setOpenKPIDrawer(true);
      },
      closeKPIDrawer: () => {
        setOpenKPIDrawer(false);
      },
      loadTemplates: (accountId?: string) => {
        getTemplates();
      },
      saveTemplate: async ({ mode, data }: any) => {
        const { editorTemplate } = KPIStore;
        try {
          let templatesFromServer: Template[] = [];
          if (mode !== 'edit') {
            const response = await addTemplate({
              variables: {
                // @ts-ignore
                accountId: ACCOUNT_ID,
                name: data.name,
                description: data.description,
                indicators: data.indicators.map((kpi: any, index: any) => ({
                  id: kpi.id,
                  order: index,
                })),
                status: 'active',
              },
            });
            // @ts-ignore
            templatesFromServer.push(response.data.kpiTemplateAdd);
            const templateAddResponse: IBaseResponse<Template[]> = {
              errorCode: 0,
              payload: templatesFromServer,
            };
            return templateAddResponse;
          } else {
            const response = await updateTemplate({
              variables: {
                id: editorTemplate.id,
                // @ts-ignore
                accountId: ACCOUNT_ID,
                name: data.name,
                description: data.description,
                indicators: data.indicators.map((kpi: any, index: any) => ({
                  id: kpi.id,
                  order: index,
                })),
                status: 'active',
              },
            });
            // @ts-ignore
            templatesFromServer.push(response.data.kpiTemplateUpdate);

            const templateUpdateResponse: IBaseResponse<Template[]> = {
              errorCode: 0,
              payload: templatesFromServer,
            };

            return templateUpdateResponse;
          }
        } catch (err: any) {
          const templateAddResponse: IBaseResponse<Template[]> = {
            errorCode: -1,
            errorMessage: err,
          };
          return templateAddResponse;
        }
      },
      deleteTemplate: async ({ row }: any) => {
        await deleteTemplate({
          variables: { id: row.original.id },
        });
        const kpiDeleteResponse: IBaseResponse<KPI[]> = {
          errorCode: 0,
        };
        return kpiDeleteResponse;
      },
      duplicateTemplate: ({ row }: any) => {
        KPIStore.editorTemplate = {
          ...row.original,
          name: 'Copy of ' + row.original.name,
          indicators: row.original.indicators.map(({ id }: any) => id),
        };
        setOpenKPIDrawer(true);
      },
      editor: {
        editorTemplateChange: (editorTemplate: any) => {
          KPIStore.editorTemplate = {
            ...editorTemplate,
          };
        },
        handleEditorSearch: (e: any) => setEditorSearch(e.target.value),
      },
    },
    models: {
      search: {
        value: searchValue,
        filterId,
      },
      openKPIDrawer,
      editorTemplate: KPIStore.editorTemplate,
      editorSearch,
      templates: KPIStore.templates.filter((template) =>
        isTemplateIncluded(template, {
          searchValue,
          filterId,
          kpisByIds: KPIStore.kpisByIds,
        }),
      ),
      templateOptions: KPIStore.templates.map(({ name }) => ({
        id: name,
        name,
      })),
    },
  };
}
